import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PaperPlaneTilt, Package, Users, Truck } from "@phosphor-icons/react";

const EMPTY_FORM = { name: "", description: "", price: "", category: "handmade",
                     fulfillment: "mail", stock: 10, image_url: "", featured: false };

const CATS = ["handmade", "digital", "general"];
const FULFILLMENT_OPTS = ["mail", "delivery", "both", "digital"];
const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];
const DELIVERY_STATUSES = ["pending", "paid", "scheduled", "out_for_delivery", "delivered", "cancelled"];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");

  if (loading) return <div className="p-10 font-mono">Loading…</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role === "buyer") return <Navigate to="/become-vendor" />;
  const isAdmin = user.role === "admin";
  const tabs = isAdmin ? [
    { k: "products", label: "Products", icon: <Package size={16}/> },
    { k: "orders", label: "Orders", icon: <Package size={16}/> },
    { k: "deliveries", label: "Deliveries", icon: <Truck size={16}/> },
    { k: "newsletter", label: "Newsletter", icon: <PaperPlaneTilt size={16}/> },
    { k: "subscribers", label: "Subscribers", icon: <Users size={16}/> },
  ] : [{ k: "products", label: "My products", icon: <Package size={16}/> }];

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <p className="overline mb-2 text-signal">// {isAdmin ? "Admin" : "Vendor"}</p>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-8">
        {isAdmin ? "Seller dashboard" : "Vendor dashboard"}
      </h1>

      <div className="flex gap-2 mb-10 border-b border-white/10 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-3 overline border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-signal text-signal" : "border-transparent hover:text-signal"
            }`} data-testid={`admin-tab-${t.k}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab === "products" && <ProductsPanel />}
      {tab === "orders" && <OrdersPanel />}
      {tab === "deliveries" && <DeliveriesPanel />}
      {tab === "newsletter" && <NewsletterPanel />}
      {tab === "subscribers" && <SubscribersPanel />}
    </main>
  );
}

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products/mine/list").then(({ data }) => setProducts(data.filter((p) => p.active !== false)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock || 1) };
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setForm(EMPTY_FORM);
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, description: p.description, price: p.price,
              category: p.category, fulfillment: p.fulfillment, stock: p.stock,
              image_url: p.image_url || "", featured: !!p.featured });
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); toast.success("Deleted"); load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image_url: fullUrl }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="products-panel">
      <form onSubmit={submit} className="lg:col-span-5 border border-white/10 bg-[#111111] p-6 space-y-3 h-fit">
        <p className="overline mb-2">{editing ? "Edit product" : "New product"}</p>
        <input className="input-sharp" placeholder="Name" required value={form.name}
               onChange={(e) => setForm({...form, name: e.target.value})} data-testid="product-name-input" />
        <textarea className="input-sharp" rows="3" placeholder="Description" required value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})} data-testid="product-desc-input" />
        <div className="grid grid-cols-2 gap-3">
          <input className="input-sharp" type="number" step="0.01" placeholder="Price USD" required value={form.price}
                 onChange={(e) => setForm({...form, price: e.target.value})} data-testid="product-price-input" />
          <input className="input-sharp" type="number" placeholder="Stock" value={form.stock}
                 onChange={(e) => setForm({...form, stock: e.target.value})} data-testid="product-stock-input" />
        </div>
        <select className="input-sharp" value={form.category}
                onChange={(e) => setForm({...form, category: e.target.value})} data-testid="product-category-select">
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input-sharp" value={form.fulfillment}
                onChange={(e) => setForm({...form, fulfillment: e.target.value})} data-testid="product-fulfillment-select">
          {FULFILLMENT_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="input-sharp" placeholder="Image URL (or upload below)" value={form.image_url}
               onChange={(e) => setForm({...form, image_url: e.target.value})} data-testid="product-image-url" />
        <input type="file" accept="image/*" onChange={upload} className="font-mono text-xs" data-testid="product-image-upload" />
        {form.image_url && <img src={form.image_url} alt="preview" className="w-full h-32 object-cover border border-white/10"/>}
        <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest cursor-pointer">
          <input type="checkbox" checked={form.featured} className="accent-[#FF073A] w-4 h-4"
                 onChange={(e) => setForm({...form, featured: e.target.checked})} data-testid="product-featured-checkbox" />
          Feature as advertisement (top of shop)
        </label>
        <div className="flex gap-2">
          <button type="submit" className="btn-signal" data-testid="product-save-btn">
            <Plus size={16}/> {editing ? "Save" : "Add product"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_FORM); }}
                    className="btn-outline-dark">Cancel</button>
          )}
        </div>
      </form>

      <div className="lg:col-span-7 space-y-3">
        {products.map((p) => (
          <div key={p.id} className="border border-white/10 bg-[#111111] p-4 flex items-center gap-4"
               data-testid={`admin-product-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt={p.name} className="w-16 h-16 object-cover border border-white/10"/>}
            <div className="flex-1">
              <p className="font-display font-bold">{p.name}</p>
              <p className="font-mono text-xs opacity-60">[{p.category}] · {p.fulfillment} · stock {p.stock}</p>
            </div>
            <p className="font-mono">${p.price.toFixed(2)}</p>
            <button onClick={() => edit(p)} className="btn-outline-dark text-xs py-2 px-3" data-testid={`edit-${p.id}`}>Edit</button>
            <button onClick={() => del(p.id)} className="text-xs font-mono text-red-600 hover:underline">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/orders").then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-3" data-testid="orders-panel">
      {orders.length === 0 ? <p className="font-mono opacity-60">No orders yet.</p> : orders.map((o) => (
        <div key={o.id} className="border border-white/10 bg-[#111111] p-4 grid md:grid-cols-7 gap-3 items-center"
             data-testid={`admin-order-${o.id}`}>
          <div className="md:col-span-2">
            <p className="font-mono text-xs opacity-50">#{o.id.slice(0, 8)}</p>
            <p className="font-display font-bold text-sm">{o.product_name}</p>
            <p className="font-mono text-xs opacity-60">{o.user_email || "guest"} · qty {o.quantity}</p>
          </div>
          <p className="font-mono text-sm">${(o.amount / 100).toFixed(2)}</p>
          <p className="font-mono text-xs uppercase tracking-widest">{o.fulfillment}</p>
          <p className="font-mono text-xs uppercase tracking-widest">{o.payment_method}</p>
          <p className="font-mono text-xs uppercase tracking-widest">{o.payment_status}</p>
          <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)}
                  className="input-sharp text-xs py-2" data-testid={`status-${o.id}`}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

function DeliveriesPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/delivery/requests").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const updateStatus = async (id, status) => {
    try { await api.put(`/delivery/requests/${id}/status`, { status }); toast.success("Status updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  if (rows.length === 0) return <p className="font-mono opacity-60" data-testid="deliveries-panel">No delivery requests yet.</p>;
  return (
    <div className="space-y-3" data-testid="deliveries-panel">
      {rows.map((r) => (
        <div key={r.id} className="border border-white/10 bg-[#111111] p-5 grid md:grid-cols-6 gap-4"
             data-testid={`delivery-row-${r.id}`}>
          <div className="md:col-span-2">
            <p className="font-mono text-xs opacity-50">#{r.id.slice(0, 8)} · {r.county_label}</p>
            <p className="font-display font-bold">{r.name}</p>
            <p className="font-mono text-xs opacity-60">{r.phone} · {r.email}</p>
          </div>
          <div className="md:col-span-2 text-sm">
            <p><span className="opacity-50 font-mono text-xs">FROM </span>{r.pickup_address}</p>
            <p><span className="opacity-50 font-mono text-xs">TO </span>{r.dropoff_address}</p>
            <p className="opacity-60 text-xs mt-1">{r.item_description}{r.preferred_time ? ` · ${r.preferred_time}` : ""}</p>
          </div>
          <p className="font-mono text-sm">${(r.amount / 100).toFixed(2)}</p>
          <div className="space-y-2">
            <p className={`font-mono text-xs uppercase tracking-widest ${r.payment_status === "paid" ? "text-signal" : "opacity-60"}`}>
              {r.payment_status}
            </p>
            <select value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)}
                    className="input-sharp text-xs py-2" data-testid={`delivery-status-${r.id}`}>
              {DELIVERY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
}

function NewsletterPanel() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/send", { subject, message });
      toast.success(`Sent to ${data.sent}/${data.total} subscribers`);
      setSubject(""); setMessage("");
    } catch { toast.error("Send failed"); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={send} className="max-w-2xl border border-white/10 bg-[#111111] p-6 space-y-4" data-testid="newsletter-panel">
      <p className="overline mb-2">Compose newsletter</p>
      <input className="input-sharp" placeholder="Subject" required value={subject}
             onChange={(e) => setSubject(e.target.value)} data-testid="nl-subject" />
      <textarea className="input-sharp" rows="8" placeholder="Message (plain text, line breaks preserved)" required
                value={message} onChange={(e) => setMessage(e.target.value)} data-testid="nl-message" />
      <button type="submit" className="btn-signal" disabled={loading} data-testid="nl-send-btn">
        <PaperPlaneTilt size={16}/> {loading ? "Sending…" : "Send to all subscribers"}
      </button>
    </form>
  );
}

function SubscribersPanel() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/newsletter/subscribers").then(({ data }) => setSubs(data)); }, []);
  return (
    <div className="border border-white/10 bg-[#111111]" data-testid="subs-panel">
      <div className="p-4 border-b border-white/10 flex justify-between">
        <p className="overline">{subs.length} active subscribers</p>
      </div>
      <ul className="divide-y divide-white/10">
        {subs.map((s) => (
          <li key={s.email} className="p-4 font-mono text-sm flex justify-between">
            <span>{s.email}</span>
            <span className="opacity-50 text-xs">{new Date(s.created_at).toLocaleDateString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
